import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useLogoutMutation } from '../../features/auth/api/authApiSlice';
import { logout, selectCurrentUser } from '../../features/auth/authSlice';
import {
  HomeIcon,
  ArrowLeftOnRectangleIcon,
  UserCircleIcon,
  Bars3Icon,
  XMarkIcon,
  BuildingOfficeIcon,
  BookOpenIcon,
  DocumentTextIcon,
  ChartBarIcon,
  CpuChipIcon,
  FolderIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import { ThemeToggle } from '../ui/theme-toggle';
import { TenantSwitcher } from '../../features/auth/components/TenantSwitcher';
import { Logger } from '../../lib/utils';
import {
  canReadNorag,
  canUseBooks,
  canUseBots,
  canUseTemplates,
} from '../../features/auth/rolePermissions';

export const MainLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className='h-screen bg-background overflow-hidden flex flex-col'>
      {/* Barra superior */}
      <header className='bg-card border-b border-border h-16 z-30 shadow-md px-4 md:px-6 flex-shrink-0'>
        <div className='flex items-center justify-between h-full'>
          {/* Logo y toggle */}
          <div className='flex items-center gap-3'>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className='p-2 rounded-md text-foreground hover:bg-accent hover:text-accent-foreground transition-colors focus:outline-none'
              aria-label='Toggle menu'
            >
              {sidebarOpen ? <XMarkIcon className='h-5 w-5' /> : <Bars3Icon className='h-5 w-5' />}
            </button>
            <Link to='/' className='flex items-center gap-2'>
              <div className='bg-primary rounded-md p-1.5 shadow-sm'>
                <BuildingOfficeIcon className='h-5 w-5 text-primary-foreground' />
              </div>
              <span className='font-semibold text-lg text-foreground hidden md:block'>Base de Conocimiento</span>
            </Link>
          </div>

          {/* Menú Usuario (tenant oculto; se deriva de empresaId) */}
          <div className='flex items-center gap-3'>
            <UserMenu />
          </div>
        </div>
      </header>

      <div className='flex flex-1 overflow-hidden'>
        {/* Sidebar - fijo en móviles, integrado en escritorio */}
        <aside
          style={{
            position: 'fixed',
            top: '4rem', // pt-16
            left: sidebarOpen ? '0' : '-16rem', // w-64 = 16rem
            width: '16rem', // w-64
            height: 'calc(100vh - 4rem)',
            zIndex: 20,
            transition: 'left 0.3s ease-in-out',
            transform: 'none'
          }}
          className={`bg-card border-r border-border shadow-sm overflow-y-auto lg:static lg:top-auto lg:left-auto lg:h-full lg:pt-0`}
        >
          <div className='h-full bg-card border-r border-border shadow-sm overflow-y-auto'>
            <SidebarContent closeSidebar={() => setSidebarOpen(false)} />
          </div>
        </aside>

        {/* Contenido principal - Expandible con desplazamiento en móviles */}
        <main
          style={{
            marginLeft: sidebarOpen ? '16rem' : '0', // w-64 = 16rem
            transition: 'margin-left 0.3s ease-in-out'
          }}
          className={`flex-1 overflow-auto p-4 sm:p-6 md:p-8 w-full bg-background lg:ml-0`}
        >
          <div className='max-w-7xl mx-auto'>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

// Menú de usuario en la cabecera
const UserMenu = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectCurrentUser);
  const [logoutApi] = useLogoutMutation();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logoutApi().unwrap();
    } catch {
      // Limpiar sesión local aunque falle el backend
    } finally {
      dispatch(logout());
      navigate('/login');
    }
  };

  // Función para obtener la etiqueta de rol según el tipo de usuario
  const getRoleLabel = (role?: string): string => {
    if (!role) return 'Usuario';

    switch (role) {
      case 'SUPERADMIN':
        return 'Superadministrador';
      case 'ADMIN':
        return 'Administrador';
      case 'JEFE_AREA':
        return 'Jefe de área';
      case 'USER':
        return 'Usuario';
      case 'ENCARGADO':
        return 'Encargado';
      case 'OPERATOR':
        return 'Usuario de empresa';
      case 'ADMIN_INTERNO':
        return 'Administrador interno';
      default:
        return 'Usuario';
    }
  };

  return (
    <div className='relative flex items-center gap-2'>
      <TenantSwitcher />
      <ThemeToggle />

      <button
        className='flex items-center gap-2 p-2 rounded-full hover:bg-accent transition-colors'
        onClick={() => setMenuOpen(!menuOpen)}
      >
        <div className='w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-medium shadow-sm'>
          {user?.email?.charAt(0).toUpperCase() || 'U'}
        </div>
        <span className='text-sm font-medium text-foreground hidden md:block'>
          {user?.email?.split('@')[0] || 'Usuario'}
        </span>
      </button>

      {menuOpen && (
        <>
          <div className='fixed inset-0 z-40' onClick={() => setMenuOpen(false)}></div>
          <div className='absolute right-0 mt-2 w-64 bg-card rounded-lg shadow-sm border border-border z-50 top-full'>
            <div className='p-4 border-b border-border bg-muted'>
              <p className='text-sm font-medium text-foreground'>{user?.email}</p>
              <p className='text-xs text-muted-foreground mt-1'>{getRoleLabel(user?.role)}</p>
              {user?.empresa?.nombre && (
                <p className='text-xs text-muted-foreground mt-1'>Tenant: {user.empresa.nombre}</p>
              )}
            </div>
            <div className='p-2'>
              <Link
                to='/perfil'
                className='flex items-center gap-2 p-2.5 text-sm text-foreground hover:bg-accent hover:text-accent-foreground rounded-md transition-colors'
                onClick={() => setMenuOpen(false)}
              >
                <UserCircleIcon className='h-5 w-5 text-muted-foreground' />
                Mi Perfil
              </Link>
              <button
                onClick={handleLogout}
                className='w-full flex items-center gap-2 p-2.5 text-sm text-destructive hover:bg-destructive/10 rounded-md transition-colors mt-1'
              >
                <ArrowLeftOnRectangleIcon className='h-5 w-5' />
                Cerrar Sesión
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Contenido de la barra lateral
interface SidebarContentProps {
  closeSidebar: () => void;
}

const SidebarContent = ({ closeSidebar }: SidebarContentProps) => {
  const user = useSelector(selectCurrentUser);

  Logger.debug('Estado del usuario en Sidebar:', {
    role: user?.role,
  });

  return (
    <div className='py-6 px-3'>
      <nav className='space-y-6'>
        <div className='px-4 py-4 space-y-1'>
          <NavItem to='/' icon={HomeIcon} text='Dashboard' closeSidebar={closeSidebar} />
          {user?.role === 'SUPERADMIN' && (
            <NavItem
              to='/empresas'
              icon={BuildingOfficeIcon}
              text='Empresas'
              closeSidebar={closeSidebar}
            />
          )}
        </div>

        {canReadNorag(user?.role) && (
          <div>
            <h3 className='px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider'>
              Conocimiento
            </h3>
            <div className='mt-3 space-y-1'>
              <NavItem
                to='/norag/dominios'
                icon={BookOpenIcon}
                text='Dominios'
                closeSidebar={closeSidebar}
              />
              <NavItem
                to='/norag/expedientes'
                icon={FolderIcon}
                text='Expedientes'
                closeSidebar={closeSidebar}
              />
              <NavItem
                to='/norag/copilot'
                icon={ChatBubbleLeftRightIcon}
                text='Copilot'
                closeSidebar={closeSidebar}
              />
              {canUseTemplates(user?.role) && (
                <NavItem
                  to='/norag/plantillas'
                  icon={DocumentTextIcon}
                  text='Plantillas'
                  closeSidebar={closeSidebar}
                />
              )}
              {canUseBooks(user?.role) && (
                <NavItem
                  to='/norag/libros'
                  icon={BookOpenIcon}
                  text='Libros'
                  closeSidebar={closeSidebar}
                />
              )}
              {canUseBots(user?.role) && (
                <NavItem
                  to='/norag/bots'
                  icon={CpuChipIcon}
                  text='Bots'
                  closeSidebar={closeSidebar}
                />
              )}
              <NavItem
                to='/norag/metricas'
                icon={ChartBarIcon}
                text='Operaciones'
                closeSidebar={closeSidebar}
              />
            </div>
          </div>
        )}

        <div>
          <h3 className='px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider'>
            Configuración
          </h3>
          <div className='mt-3 space-y-1'>
            <NavItem to='/perfil' icon={UserCircleIcon} text='Mi Perfil' closeSidebar={closeSidebar} />
          </div>
        </div>
      </nav>

      {/* Footer con versión */}
      <div className='mt-auto'>
        <div className='border-t border-border pt-4'>
          <p className='text-xs text-muted-foreground text-center'>Base de Conocimiento v1.0</p>
        </div>
      </div>
    </div>
  );
};

// Item de navegación en la sidebar
interface NavItemProps {
  to: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  text: string;
  badge?: string;
  closeSidebar: () => void;
}

const NavItem = ({ to, icon: Icon, text, badge, closeSidebar }: NavItemProps) => {
  // Cerrar el sidebar en dispositivos móviles cuando se hace clic en un enlace
  const handleClick = () => {
    if (window.innerWidth < 1024) {
      closeSidebar();
    }
  };

  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex items-center justify-between px-4 py-3 rounded-md transition-colors ${
          isActive
            ? 'bg-accent text-accent-foreground font-medium'
            : 'text-foreground hover:bg-muted'
        }`
      }
      onClick={handleClick}
    >
      {({ isActive }) => (
        <>
          <div className='flex items-center'>
            <Icon
              className={`h-5 w-5 mr-3 flex-shrink-0 ${
                isActive ? 'text-primary' : 'text-muted-foreground'
              }`}
            />
            <span className='text-sm'>{text}</span>
          </div>

          {badge && (
            <span className='ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary'>
              {badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
};
